import { useState, useEffect, useMemo, useRef } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useWebSocket } from '../hooks/useWebSocket'
import {
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Switch,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material'
import {
  Add as AddIcon,
  ViewKanban as BoardIcon,
  TableRows as TableIcon,
} from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'

import {
  clientsAtom,
  clientsLoadingAtom,
  WorkflowStage,
  workflowStageDefinitionsAtom,
  STAGE_TEAM_FIELD,
} from '../state/clients'
import { userAtom } from '../state/auth'
import { clientsApi, usersApi, Client, User } from '../services/api'
import ClientPipelineRow from '../components/client-pipeline/ClientPipelineRow'
import ClientPipelineColumn from '../components/client-pipeline/ClientPipelineColumn'
import { ClientCardDragPreview } from '../components/client-pipeline/ClientCard'
import { getDaysInStage } from '../components/client-pipeline/clientStatusChips'
import AdvanceStageDialog from '../components/client-detail/AdvanceStageDialog'
import AssignTeamDialog from '../components/clients/AssignTeamDialog'
import QuickSmsDialog from '../components/client-pipeline/QuickSmsDialog'
import QuickNoteDialog from '../components/client-pipeline/QuickNoteDialog'
import AddClientDialog, { NewClientData } from '../components/clients/AddClientDialog'
import { useNotification } from '../hooks/useNotification'
import { isAdminOrManager } from '../utils/roles'
import { getEnhancedTableStyles } from '../utils/tableStyles'
import SortableTableHeader, { SortOrder } from '../components/common/table/SortableTableHeader'
import FilterBar, { FilterBarFilter } from '../components/common/table/FilterBar'
import TablePaginationBar from '../components/common/table/TablePaginationBar'
import { useTablePagination } from '../components/common/table/useTablePagination'

// Droppable id for the column holding clients left behind by a deactivated
// stage. Not a stage id, and deliberately not one anything can be dropped on.
const STRANDED_COLUMN_ID = '__stranded__'

// What is left of the viewport once the app shell, the page heading and the
// filter bar have taken theirs. Measured against this page rather than
// guessed, and the reason a column scrolls itself instead of the window.
const BOARD_HEIGHT = 'calc(100dvh - 248px)'

// Whose stage this is, in the words the team uses for themselves.
const STAGE_TEAM_LABELS: Record<string, string | undefined> = {
  clientSuccess: 'Client Success',
  resumeWriter: 'Resume Writer',
  coach: 'Coach',
}

export default function ClientPipeline() {
  const navigate = useNavigate()
  const theme = useTheme()
  const [clients, setClients] = useAtom(clientsAtom)
  const [loading, setLoading] = useAtom(clientsLoadingAtom)
  const [user] = useAtom(userAtom)
  const [workflowStages] = useAtom(workflowStageDefinitionsAtom)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [selectedStages, setSelectedStages] = useState<string[]>([])
  const [showComplete, setShowComplete] = useState(false)
  const [orderBy, setOrderBy] = useState<string>('stage')
  const [order, setOrder] = useState<SortOrder>('asc')
  // The board is where the pipeline is worked; the table is the same clients
  // laid out for reading. Filters, sort and Show complete are shared, so
  // switching view never changes who is on screen.
  const [view, setView] = useState<'board' | 'table'>('board')
  const { showSuccess, showError } = useNotification()

  const tableStyles = getEnhancedTableStyles(theme)

  // Stage definitions are admin configuration (Stage Gates), never code
  // constants — the table's stage options and ordering both come from here so
  // deactivating or reordering a stage still drives this page.
  const activeStages = useMemo(
    () =>
      workflowStages
        .filter((s) => s.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)),
    [workflowStages]
  )
  const stageIds = useMemo(() => activeStages.map((s) => s.stageId), [activeStages])
  const stageLabels = useMemo(
    () => Object.fromEntries(activeStages.map((s) => [s.stageId, s.label])),
    [activeStages]
  )
  // MWA-391 — the email each stage sends when a client enters it (Stage Gates).
  const stageEntryEmails = useMemo(
    () => Object.fromEntries(activeStages.map((s) => [s.stageId, s.entryEmail ?? null])),
    [activeStages]
  )

  // Stage gate dialog state
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false)
  const [pendingAdvance, setPendingAdvance] = useState<{
    clientId: string
    clientName: string
    currentStage: WorkflowStage
    targetStage: WorkflowStage
    previousClients: Client[]
    resumeApproved?: boolean
    internalResumeApproved?: boolean
    internalApprovedBy?: string
    serviceAgreementApproved?: boolean
    teamAssignment?: { clientSuccess?: string; resumeWriter?: string; coach?: string }
  } | null>(null)

  // Quick action dialog state
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [assignTeamOpen, setAssignTeamOpen] = useState(false)
  const [smsDialogOpen, setSmsDialogOpen] = useState(false)
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false)

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  // WebSocket handlers for real-time sync
  const wsHandlers = useMemo(
    () => ({
      client_created: (message: { data: unknown }) => {
        const newClient = message.data as Client
        setClients((prev) => {
          if (prev.some((c) => c.id === newClient.id)) return prev
          return [...prev, newClient]
        })
      },
      client_updated: (message: { data: unknown }) => {
        const updatedClient = message.data as Client
        setClients((prev) => prev.map((c) => (c.id === updatedClient.id ? { ...c, ...updatedClient } : c)))
      },
      workflow_advanced: (message: { data: unknown }) => {
        const updatedClient = message.data as Client
        setClients((prev) => prev.map((c) => (c.id === updatedClient.id ? { ...c, ...updatedClient } : c)))
      },
      stage_gate_updated: () => {
        // Refetch all clients to get updated incompleteTaskCount values
        loadClients()
      },
      // MWA-390 — a broadcast missed while the socket was down is gone for good,
      // so the board reloads once the connection is back.
      ws_reconnected: () => {
        loadClients()
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setClients]
  )

  useWebSocket(wsHandlers)

  useEffect(() => {
    loadClients()
    loadUsers()
  }, [])

  // Default filter: managers see all clients, others see only their own
  const defaultFilterSet = useRef(false)
  useEffect(() => {
    if (user?.email && !defaultFilterSet.current) {
      if (!isManager) {
        setSelectedUsers([user.email])
      }
      defaultFilterSet.current = true
    }
  }, [user, isManager])

  const loadClients = async () => {
    setLoading(true)
    setError(null)
    try {
      const clients = await clientsApi.list({ all: true })
      setClients(clients ?? [])
    } catch (err) {
      console.error('Failed to load clients:', err)
      setError('Failed to load clients. Please try again.')
      setClients([])
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const usersList = await usersApi.list()
      setUsers(usersList ?? [])
    } catch (err) {
      console.error('Failed to load users:', err)
      // Don't show error to user - this is just for name lookups
    }
  }

  // Create email-to-name mapping
  const emailToName = useMemo(() => {
    const map = new Map<string, string>()
    users.forEach((user) => {
      map.set(user.email, user.name)
    })
    return map
  }, [users])

  // Get unique team members from all clients with their names
  const teamMembers = useMemo(() => {
    const memberEmails = new Set<string>()
    if (user?.email) memberEmails.add(user.email)
    clients.forEach((client) => {
      if (client.assignedTo) memberEmails.add(client.assignedTo)
      if (client.clientSuccess) memberEmails.add(client.clientSuccess)
      if (client.resumeWriter) memberEmails.add(client.resumeWriter)
      if (client.coach) memberEmails.add(client.coach)
    })
    return Array.from(memberEmails)
      .map((email) => ({
        email,
        name: emailToName.get(email) || email.split('@')[0],
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [clients, emailToName, user])

  /**
   * Move a client to another stage. This is the single path a stage change
   * takes — it carries every guard the drag-and-drop board used to own, because
   * losing any of them would silently weaken gate enforcement:
   *
   *  - backward moves write the stage directly, with no gate checks
   *  - four specific forward moves always raise AdvanceStageDialog
   *  - an `incomplete_tasks` rejection reopens that dialog so staff can
   *    see the outstanding tasks and override
   */
  const changeStage = async (client: Client, nextStage: string) => {
    const clientId = client.id
    const newStage = nextStage as WorkflowStage

    if (client.workflowStage === newStage) return

    // A client sitting in a deactivated stage has no index, so any move reads as
    // forward and therefore runs the gate checks rather than skipping them.
    const currentIndex = stageIds.indexOf(client.workflowStage as WorkflowStage)
    const targetIndex = stageIds.indexOf(newStage)
    const isMovingForward = targetIndex > currentIndex

    const previousClients = [...clients]

    // Optimistically update
    setClients(
      clients.map((c) =>
        c.id === clientId
          ? { ...c, workflowStage: newStage as Client['workflowStage'], updatedAt: new Date().toISOString() }
          : c
      )
    )

    // Moving backward: update the stage directly, no gate checks or dialog
    if (!isMovingForward) {
      try {
        const updatedClient = await clientsApi.update({
          id: clientId,
          workflowStage: newStage as Client['workflowStage'],
        })
        setClients(clients.map((c) => (c.id === clientId ? { ...c, ...updatedClient } : c)))
        showSuccess(`Client moved back to ${stageLabels[newStage] || newStage}`)
      } catch (err) {
        console.error('Failed to move client:', err)
        setClients(previousClients)
        showError('Failed to move client')
      } finally {
      }
      return
    }

    const teamAssignment = {
      clientSuccess: client.clientSuccess,
      resumeWriter: client.resumeWriter,
      coach: client.coach,
    }

    const hasNoTeam = !client.clientSuccess && !client.resumeWriter && !client.coach
    const isNorthStarToStrategy = client.workflowStage === 'north_star' && newStage === 'strategy_session'
    const isDeliverablesAdvance =
      client.workflowStage === 'deliverables_in_progress' && newStage === 'deliverables_complete'
    const targetSendsEmail = Boolean(stageEntryEmails[newStage])

    // Always show the advance dialog when:
    // - No team is assigned (team assignment is critical for notifications)
    // - Moving from north_star → strategy_session (to show NS approval status)
    // - Moving from deliverables_in_progress → deliverables_complete (to show resume approval status)
    // - Moving into any stage that is set to email the client (Stage Gates), so staff see it first
    if (hasNoTeam || isNorthStarToStrategy || isDeliverablesAdvance || targetSendsEmail) {
      setClients(previousClients) // Revert optimistic update; dialog handles advancing
      setPendingAdvance({
        clientId,
        clientName: client.name,
        currentStage: client.workflowStage,
        targetStage: newStage,
        previousClients,
        resumeApproved: client.resumeApproved,
        internalResumeApproved: client.internalResumeApproved,
        internalApprovedBy: client.internalApprovedBy,
        serviceAgreementApproved: client.serviceAgreementApproved,
        teamAssignment,
      })
      setAdvanceDialogOpen(true)
      return
    }

    // Use advanceWorkflowStage to trigger notifications and ownership transfer
    try {
      const result = await clientsApi.advanceWorkflowStage(clientId, newStage as Client['workflowStage'])
      showSuccess(`Client moved to ${result.stageLabel}`)
    } catch (err: unknown) {
      console.error('Failed to update client stage:', err)
      setClients(previousClients)

      // Stage gate rejection — open the dialog so the outstanding tasks are
      // visible and can be overridden, rather than failing with a toast.
      if (err instanceof Error && err.message.includes('incomplete_tasks')) {
        setPendingAdvance({
          clientId,
          clientName: client.name,
          currentStage: client.workflowStage,
          targetStage: newStage,
          previousClients,
          resumeApproved: client.resumeApproved,
          teamAssignment,
        })
        setAdvanceDialogOpen(true)
      } else {
        showError('Failed to update client stage')
      }
    } finally {
    }
  }

  const handleAdvanceDialogClose = () => {
    setAdvanceDialogOpen(false)
    setPendingAdvance(null)
  }

  const handleAdvanceDialogSuccess = () => {
    loadClients()
    handleAdvanceDialogClose()
  }

  const handleClientClick = (client: Client) => {
    navigate(`/clients/${client.id}`)
  }

  // Quick action handlers
  const handleAssignTeam = (client: Client) => {
    setSelectedClient(client)
    setAssignTeamOpen(true)
  }

  const handleSendSms = (client: Client) => {
    setSelectedClient(client)
    setSmsDialogOpen(true)
  }

  const handleAddNote = (client: Client) => {
    setSelectedClient(client)
    setNoteDialogOpen(true)
  }

  const handleTeamAssigned = (updatedClient: Client) => {
    setClients((prev) => prev.map((c) => (c.id === updatedClient.id ? { ...c, ...updatedClient } : c)))
    showSuccess('Team assigned successfully')
    setAdvanceDialogOpen(false) // Close advance dialog as team is now assigned
  }

  const handleOpenAssignTeamFromAdvance = () => {
    if (pendingAdvance) {
      const client = clients.find((c) => c.id === pendingAdvance.clientId)
      if (client) {
        setSelectedClient(client)
        setAssignTeamOpen(true)
      }
    }
  }

  const handleAddClient = async (clientData: NewClientData) => {
    try {
      const newClient = await clientsApi.create(clientData)
      setClients((prev) => {
        if (prev.some((c) => c.id === newClient.id)) return prev
        return [...prev, newClient]
      })
      setAddClientDialogOpen(false)
      showSuccess('Client added successfully')
    } catch (err) {
      console.error('Error adding client:', err)
      showError('Failed to add client')
      throw err
    }
  }

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  const filterConfig: FilterBarFilter[] = useMemo(
    () => [
      {
        name: 'team',
        label: 'Team member',
        options: teamMembers.map((m) => ({ value: m.email, label: m.name })),
        value: selectedUsers,
      },
      {
        name: 'stage',
        label: 'Stage',
        options: activeStages.map((s) => ({ value: s.stageId, label: s.label })),
        value: selectedStages,
      },
    ],
    [teamMembers, selectedUsers, activeStages, selectedStages]
  )

  const handleFilterChange = (name: string, value: unknown) => {
    if (name === 'team') setSelectedUsers(value as string[])
    if (name === 'stage') setSelectedStages(value as string[])
  }

  const clearFilters = () => {
    setSelectedUsers([])
    setSelectedStages([])
  }

  // Rows come from the flat client list rather than a per-stage grouping, so a
  // client whose stage an admin has since deactivated still appears instead of
  // vanishing from the page entirely.
  const visibleClients = useMemo(() => {
    let rows = clients ?? []

    if (!showComplete) {
      rows = rows.filter((c) => c.workflowStage !== 'complete')
    }

    if (selectedStages.length > 0) {
      rows = rows.filter((c) => selectedStages.includes(c.workflowStage))
    }

    // Only matches assignedTo + the active team role for the client's current
    // stage, so team members only see clients when their role is active.
    if (selectedUsers.length > 0) {
      rows = rows.filter((c) => {
        if (selectedUsers.includes(c.assignedTo || '')) return true
        const activeField = STAGE_TEAM_FIELD[c.workflowStage as WorkflowStage]
        if (activeField && selectedUsers.includes((c[activeField] as string) || '')) return true
        return false
      })
    }

    const direction = order === 'asc' ? 1 : -1
    const byName = (a: Client, b: Client) => a.name.localeCompare(b.name)

    return [...rows].sort((a, b) => {
      switch (orderBy) {
        case 'name':
          return byName(a, b) * direction
        case 'stage': {
          // Unconfigured stages sort last rather than ahead of everything.
          const ai = stageIds.indexOf(a.workflowStage as WorkflowStage)
          const bi = stageIds.indexOf(b.workflowStage as WorkflowStage)
          const av = ai === -1 ? Number.MAX_SAFE_INTEGER : ai
          const bv = bi === -1 ? Number.MAX_SAFE_INTEGER : bi
          return av === bv ? byName(a, b) : (av - bv) * direction
        }
        case 'team': {
          const at = a.assignedTo || ''
          const bt = b.assignedTo || ''
          return at === bt ? byName(a, b) : at.localeCompare(bt) * direction
        }
        case 'days': {
          const ad = getDaysInStage(a.updatedAt)
          const bd = getDaysInStage(b.updatedAt)
          return ad === bd ? byName(a, b) : (ad - bd) * direction
        }
        default:
          return 0
      }
    })
  }, [clients, showComplete, selectedStages, selectedUsers, orderBy, order, stageIds])

  const pageCtl = useTablePagination(
    visibleClients,
    `${orderBy}|${order}|${selectedUsers.join(',')}|${selectedStages.join(',')}|${showComplete}`
  )

  // Clients whose stage an admin has deactivated have no column of their own.
  // They get a catch-all at the end rather than vanishing from the board,
  // because a client nobody can see is a client nobody moves.
  const board = useMemo(() => {
    const byStage = new Map<string, Client[]>()
    activeStages.forEach((s) => byStage.set(s.stageId, []))
    const stranded: Client[] = []
    visibleClients.forEach((client) => {
      const column = byStage.get(client.workflowStage)
      if (column) column.push(client)
      else stranded.push(client)
    })
    return { byStage, stranded }
  }, [visibleClients, activeStages])

  // The Complete column goes with its cards: Show complete off hides the
  // column itself, not just the clients inside it.
  const boardStages = useMemo(
    () => (showComplete ? activeStages : activeStages.filter((s) => s.stageId !== 'complete')),
    [activeStages, showComplete]
  )

  // A short drag threshold, so that clicking a card's name or its menu is a
  // click rather than the start of a drag that never went anywhere.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const [draggingId, setDraggingId] = useState<string | null>(null)
  const draggingClient = draggingId ? clients.find((c) => c.id === draggingId) : undefined

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null)
    const { active, over } = event
    if (!over) return
    const target = String(over.id)
    // The catch-all is a place to be rescued from, not moved into.
    if (target === STRANDED_COLUMN_ID) return
    const client = clients.find((c) => c.id === active.id)
    if (client) changeStage(client, target)
  }

  // The board loads as itself: the stage columns are known before any client
  // is, so they can be drawn with their own skeletons rather than replaced by
  // a spinner that says nothing about what is arriving. The table has no such
  // structure to show ahead of its rows, so it keeps the spinner.
  // Stage definitions arrive on their own schedule, so until they do there are
  // no columns to draw and the spinner is still the honest answer.
  const firstLoad = loading && (clients ?? []).length === 0
  if (firstLoad && (view === 'table' || activeStages.length === 0)) {
    return <LoadingSpinner />
  }

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', mb: { xs: 2, sm: 3 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Client Milestones
          </Typography>
          <PageHelpButton articleId="clients-understanding-workflow-stages" />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next) => next && setView(next)}
            aria-label="Pipeline view"
          >
            <ToggleButton value="board" aria-label="Board view">
              <Tooltip title="Board — move clients between stages">
                <BoardIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table — every client at a glance, read only">
                <TableIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={
              <Switch
                checked={showComplete}
                onChange={(e) => setShowComplete(e.target.checked)}
                size="small"
              />
            }
            label="Show complete"
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddClientDialogOpen(true)}>
            Add Client
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <FilterBar filters={filterConfig} onChange={handleFilterChange} onClearAll={clearFilters} />

      {view === 'board' ? (
        <DndContext
          sensors={sensors}
          // The column under the cursor is the one being dropped into.
          // dnd-kit's default compares rectangles, and a card dragged half out
          // of its own column still overlaps that column most, so every drop
          // resolved back to the stage it started in and did nothing.
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setDraggingId(null)}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 1,
              scrollSnapType: { xs: 'x mandatory', sm: 'none' },
              alignItems: 'stretch',
              // The board owns the screen it is given: a stage holding fifty
              // clients scrolls inside its own column rather than growing the
              // page, so the other columns stay where they were left.
              height: BOARD_HEIGHT,
            }}
          >
            {boardStages.map((stage) => (
              <ClientPipelineColumn
                key={stage.stageId}
                stageId={stage.stageId}
                label={stage.label}
                color={stage.color}
                teamLabel={STAGE_TEAM_LABELS[stage.teamRole ?? '']}
                clients={board.byStage.get(stage.stageId) ?? []}
                // Only the first load draws skeletons. A later refetch — a
                // stage gate changing, say — would otherwise replace every
                // card on the board with placeholders for as long as it ran.
                loading={firstLoad}
                onClientClick={handleClientClick}
                onAssignTeam={handleAssignTeam}
                onSendSms={handleSendSms}
                onAddNote={handleAddNote}
              />
            ))}
            {board.stranded.length > 0 && (
              <ClientPipelineColumn
                stageId={STRANDED_COLUMN_ID}
                label="Unassigned stage"
                clients={board.stranded}
                onClientClick={handleClientClick}
                onAssignTeam={handleAssignTeam}
                onSendSms={handleSendSms}
                onAddNote={handleAddNote}
                emptyText="Nobody is stranded"
              />
            )}
          </Box>
          {/* No drop animation: the card lands in its new column the moment it
              is released, as it did before the overlay existed, rather than
              gliding back to the column it is leaving. */}
          <DragOverlay dropAnimation={null}>
            {draggingClient ? <ClientCardDragPreview client={draggingClient} /> : null}
          </DragOverlay>
        </DndContext>
      ) : visibleClients.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            No clients to show
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(clients ?? []).length === 0
              ? 'Add your first client to start the pipeline.'
              : 'No clients match the current filters.'}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={tableStyles.tableContainer}>
          <Box sx={{ overflow: 'auto', maxHeight: '70vh', '& table': { minWidth: 1120 } }}>
            <Table stickyHeader sx={tableStyles.table}>
              <TableHead>
                <TableRow>
                  <SortableTableHeader id="name" label="Client" orderBy={orderBy} order={order} onSort={handleSort} />
                  <SortableTableHeader id="stage" label="Stage" orderBy={orderBy} order={order} onSort={handleSort} />
                  <SortableTableHeader id="team" label="Team" orderBy={orderBy} order={order} onSort={handleSort} />
                  <SortableTableHeader
                    id="days"
                    label="In stage"
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align="right"
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                  <TableCell>Deliverables</TableCell>
                  <TableCell>Needs attention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageCtl.paginated.map((client) => {
                  const stageIsUnconfigured = !stageIds.includes(client.workflowStage as WorkflowStage)
                  return (
                    <ClientPipelineRow
                      key={client.id}
                      client={client}
                      currentStageLabel={stageLabels[client.workflowStage] || client.workflowStage}
                      stageIsUnconfigured={stageIsUnconfigured}
                      onClientClick={handleClientClick}
                      showAssignee={selectedUsers.length === 0}
                    />
                  )
                })}
              </TableBody>
            </Table>
          </Box>
          <TablePaginationBar
            count={pageCtl.count}
            page={pageCtl.page}
            rowsPerPage={pageCtl.rowsPerPage}
            setPage={pageCtl.setPage}
            setRowsPerPage={pageCtl.setRowsPerPage}
          />
        </Paper>
      )}

      {/* Stage Gate Dialog */}
      {pendingAdvance && (
        // The three approval props below were collected into pendingAdvance
        // but never handed over, so the dialog fell back to its own defaults:
        // it told every staff member the resume was not internally approved,
        // and warned about a missing service agreement, however the client
        // record actually stood.
        <AdvanceStageDialog
          open={advanceDialogOpen}
          onClose={handleAdvanceDialogClose}
          clientId={pendingAdvance.clientId}
          clientName={pendingAdvance.clientName}
          currentStage={pendingAdvance.currentStage}
          targetStage={pendingAdvance.targetStage}
          // A client rescued from a deactivated stage has no label to look up,
          // and the dialog read "Moving Penny from to Market Ready".
          currentStageLabel={stageLabels[pendingAdvance.currentStage] || pendingAdvance.currentStage}
          targetStageLabel={stageLabels[pendingAdvance.targetStage] || pendingAdvance.targetStage}
          onAdvanced={handleAdvanceDialogSuccess}
          resumeApproved={pendingAdvance.resumeApproved}
          internalResumeApproved={pendingAdvance.internalResumeApproved}
          internalApprovedBy={pendingAdvance.internalApprovedBy}
          serviceAgreementApproved={pendingAdvance.serviceAgreementApproved}
          teamAssignment={pendingAdvance.teamAssignment}
          onAssignTeam={handleOpenAssignTeamFromAdvance}
          entryEmail={stageEntryEmails[pendingAdvance.targetStage]}
        />
      )}

      {/* Quick Action Dialogs */}
      <AssignTeamDialog
        open={assignTeamOpen}
        onClose={() => setAssignTeamOpen(false)}
        client={selectedClient}
        onAssigned={handleTeamAssigned}
      />

      <QuickSmsDialog open={smsDialogOpen} onClose={() => setSmsDialogOpen(false)} client={selectedClient} />

      <QuickNoteDialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} client={selectedClient} />

      <AddClientDialog
        open={addClientDialogOpen}
        onClose={() => setAddClientDialogOpen(false)}
        onAdd={handleAddClient}
      />
    </Box>
  )
}
